/* oxlint-disable react/static-components -- these render APIs intentionally receive component references */
import moment from 'moment'
import { ReactElement } from 'react'
import { lastCommIcon, lastCommLabel, type LastComm } from 'lib/lastCommMedium'

interface FollowUpDetails {
  isConnected?: boolean
  isScheduled?: boolean
  appointmentDate?: Date | string
  description?: string
}

interface FormDetails {
  patientName?: string
  email?: string
  phone?: string
  title?: string
}

interface SchedulerDetails {
  appointmentDate?: Date | string
  appointmentType?: string
}

export interface CallDetails {
  direction?: string
  duration?: number
  answered?: boolean
  answerer?: string
  summary?: string
  outcomes?: string[]
}

interface LastCommTooltipProps {
  /** Stored contact descriptor takes precedence over optional activity enrichment. */
  communication?: LastComm
  activityType?: string
  activityDate?: Date | string
  followUp?: FollowUpDetails
  summary?: string
  form?: FormDetails
  scheduler?: SchedulerDetails
  call?: CallDetails
  /** The call detail is still in flight; title and timestamp already render. */
  loadingCall?: boolean
}

/**
 * A call's `description` is the direction label the ingestion wrote ("Inbound
 * Call", "Missed Call"), which the cell has already said. Showing it as the
 * tooltip's body is what made hovering pointless.
 */
const REDUNDANT_CALL_DESCRIPTION = /^(inbound|outbound|missed|answered)\s+call$/i

function titleFor(activityType?: string, call?: CallDetails): string {
  if (activityType === `Call`) {
    const direction = call?.direction?.toLowerCase()
    if (direction === `inbound`) return `Inbound call`
    if (direction === `outbound`) return `Outbound call`
    return `Call`
  }
  switch (activityType) {
    case `FollowUp`:
      return `Follow up`
    case `Imported Form`:
      return `Form`
    case `Online Scheduler`:
      return `Scheduler`
    case `Virtual Consultation`:
      return `Consultation`
    default:
      return activityType ?? `Last communication`
  }
}

/**
 * The one thing the cell genuinely cannot show. "Today" is not an answer to
 * "when today", which is the whole reason to hover a relative date.
 */
function metaFor(activityDate?: Date | string, call?: CallDetails): string[] {
  const parts: string[] = []
  if (activityDate) parts.push(moment(activityDate).format(`MMM D, YYYY [at] h:mm A`))
  if (!call) return parts

  if (call.duration) parts.push(formatCallDuration(call.duration))
  if (call.answered === false) parts.push(`Missed`)
  else if (call.answerer) parts.push(`Answered by ${call.answerer}`)
  return parts
}

/**
 * "10s", "1m 40s". The mm:ss the call cards use reads as a clock time next to a
 * real clock time, so a ten second call rendered as "00:10" right after
 * "6:33 AM" and looked like a second timestamp.
 */
function formatCallDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  if (minutes < 60) return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

function DetailLines({ lines }: { lines: string[] }): ReactElement {
  return (
    <div className="flex flex-col gap-1 leading-snug">
      {lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </div>
  )
}

/**
 * Content for the Last Communication tooltip. Three or four lines: what it was,
 * exactly when (plus how long and who took it, for calls), what was said, and
 * what it led to. No repeated header, no generic lightbulb, no bullet lists.
 */
export function LastCommTooltip({
  communication,
  activityType,
  activityDate,
  followUp,
  summary,
  form,
  scheduler,
  call,
  loadingCall,
}: LastCommTooltipProps): ReactElement {
  const Icon = lastCommIcon(communication ? communication.type : activityType)
  const meta = metaFor(communication?.date ?? activityDate, call)

  const isFollowUp = activityType === `FollowUp` || activityType === `Follow Up`
  const isForm = activityType === `Form` || activityType === `Imported Form`
  const isScheduler = activityType === `Scheduler` || activityType === `Online Scheduler`

  const detailLines: string[] = []
  if (isFollowUp && followUp) {
    const outcome = [
      followUp.isConnected ? `Connected` : `Not connected`,
      followUp.isScheduled
        ? followUp.appointmentDate
          ? `appointment ${moment(followUp.appointmentDate).format(`MMM D`)}`
          : `appointment scheduled`
        : `no appointment`,
    ].join(` · `)
    detailLines.push(outcome)
    if (followUp.description) detailLines.push(followUp.description)
  } else if (isForm && form) {
    if (form.title) detailLines.push(form.title)
    const identity = [form.patientName, form.email, form.phone].filter(Boolean).join(` · `)
    if (identity) detailLines.push(identity)
  } else if (isScheduler && scheduler) {
    const appointment = [
      scheduler.appointmentType,
      scheduler.appointmentDate && moment(scheduler.appointmentDate).format(`MMM D, YYYY`),
    ]
      .filter(Boolean)
      .join(` on `)
    if (appointment) detailLines.push(appointment)
  }

  const callSummary = call?.summary
  const activitySummary = summary && !REDUNDANT_CALL_DESCRIPTION.test(summary.trim()) ? summary : undefined
  const body = callSummary ?? activitySummary

  return (
    <div className="flex max-w-72 flex-col gap-2 text-xs">
      {/* Title and timestamp are one block: the meta line indents to the title's
          text, not the icon, so the header has a single left edge. */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-[13px] font-semibold leading-none">
          {Icon && <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />}
          <span>{communication ? lastCommLabel(communication) : titleFor(activityType, call)}</span>
        </div>
        {meta.length > 0 && <span className="pl-[22px] leading-snug tabular-nums opacity-75">{meta.join(` · `)}</span>}
      </div>

      {body && <p className="line-clamp-4 leading-relaxed text-pretty">{body}</p>}
      {!body && loadingCall && <span className="leading-snug opacity-60">Loading summary…</span>}

      {detailLines.length > 0 && <DetailLines lines={detailLines} />}

      {call?.outcomes && call.outcomes.length > 0 && (
        <span className="border-t border-background/15 pt-2 leading-snug opacity-75">{call.outcomes.join(` · `)}</span>
      )}
    </div>
  )
}
